import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    BannedTeamsContextProps,
    BannedTeamsContextValues,
} from "../types/contexts";
import { BannedTeamsList, SortColumns } from "../types/bannedTeams";

import { SortOrders } from "../../../../../../types/shared";

const BannedTeamsContext = createContext<BannedTeamsContextValues | null>(null);

export const useBannedTeamsContext = () => {
    const context = useContext(BannedTeamsContext);
    if (!context) {
        throw new Error(
            "useBannedTeamsContext must be used within a BannedTeamsProvider",
        );
    }
    return context;
};

export const BannedTeamsProvider: React.FC<BannedTeamsContextProps> = props => {
    const { children, initialUmpires } = props;
    const [umpires, setUmpires] = useState<BannedTeamsList[]>([]);

    useEffect(() => {
        setUmpires(initialUmpires);
    }, [initialUmpires]);

    const [searchValue, setSearchValue] = useState<string>("");
    const [sortColumn, setSortColumn] = useState<SortColumns>("name");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");

    const filteredUmpires = useMemo(() => {
        if (searchValue) {
            const search = searchValue.toLowerCase();
            return umpires.filter(u => {
                return (
                    u.name.toLowerCase().includes(search) ||
                    u.bannedTeams.some(t => t.toLowerCase().includes(search))
                );
            });
        }
        return umpires;
    }, [umpires, searchValue]);

    const sortedUmpires = useMemo(() => {
        let aValue: any;
        let bValue: any;

        return [...filteredUmpires].sort((a, b) => {
            switch (sortColumn) {
                case "name":
                    aValue = a.name;
                    bValue = b.name;
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredUmpires, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            umpires: sortedUmpires,
            setUmpires,
            totalItems: sortedUmpires.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
            searchValue,
            setSearchValue,
        }),
        [sortedUmpires],
    );

    return (
        <BannedTeamsContext.Provider value={contextValue}>
            {children}
        </BannedTeamsContext.Provider>
    );
};
